import type React from 'react';
import Input from '../atoms/Input';
import TextArea from '../atoms/TextArea';
import Label, { type LabelProps } from '../atoms/Label';

export interface FormFieldProps {
  id: string;
  label?: string;
  labelProps?: Omit<LabelProps, 'htmlFor' | 'children'>;
  error?: string;
  hint?: string;
  required?: boolean;
  containerClassName?: string;
  as?: 'input' | 'textarea';
  // Input/TextArea common props
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  // Input specific props
  type?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  variant?: 'default' | 'error';
  // TextArea specific props  
  autoResize?: boolean;
  maxHeight?: number;
  minHeight?: number;
}

const getAriaDescribedBy = (id: string, error?: string, hint?: string): string | undefined => {
  if (error) {
    return `${id}-error`;
  }
  if (hint) {
    return `${id}-hint`;
  }
  return undefined;
};

const FormField: React.FC<FormFieldProps> = ({
  id,
  label,
  labelProps,
  error,
  hint,
  required = false,
  containerClassName = '',
  className = '',
  as = 'input',
  // Extract specific props
  value,
  onChange,
  placeholder,
  disabled,
  type,
  leftIcon,
  rightIcon,
  variant,
  autoResize,
  maxHeight,
  minHeight
}) => {
  const hasError = !!error;
  const fieldVariant = hasError ? 'error' : variant;

  return (
    <div className={`space-y-1 ${containerClassName}`}>
      {/* Label */}
      {label && (
        <Label 
          htmlFor={id} 
          required={required}
          variant={hasError ? 'error' : 'default'}
          {...labelProps}
        >
          {label}
        </Label>
      )}
      
      {/* Input or TextArea */}
      {as === 'textarea' ? (
        <TextArea
          id={id}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`${className} ${hasError ? 'border-danger-border focus:border-danger focus:ring-danger' : ''}`}
          aria-invalid={hasError}
          aria-describedby={getAriaDescribedBy(id, error, hint)}
          autoResize={autoResize}
          maxHeight={maxHeight}
          minHeight={minHeight}
        />
      ) : (
        <Input
          id={id}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          type={type}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          variant={fieldVariant}
          error={hasError}
          className={className}
          aria-invalid={hasError}
          aria-describedby={getAriaDescribedBy(id, error, hint)}
        />
      )}
      
      {/* Error Message */}
      {error && (
        <p 
          id={`${id}-error`}
          className="text-xs text-danger"
          role="alert"
        >
          {error}
        </p>
      )}
      
      {/* Hint (when no error) */}
      {hint && !hasError && (
        <p 
          id={`${id}-hint`}
          className="text-xs text-text-muted"
        >
          {hint}
        </p>
      )}
    </div>
  );
};

FormField.displayName = 'FormField';

export default FormField;